import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { GripVertical } from "lucide-react";
import { FollowUpBadge } from "./LeadDetail";
import { STATUSES } from "./leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ lead, onOpen, dragging }) => (
  <motion.div
    layout
    layoutId={lead.id}
    draggable
    data-testid={`kanban-card-${lead.id}`}
    onDragStart={(e) => { e.dataTransfer.setData("text/plain", lead.id); e.dataTransfer.effectAllowed = "move"; }}
    onClick={() => onOpen(lead)}
    className={`group bg-white border hairline p-4 cursor-grab active:cursor-grabbing hover:border-ink transition-colors ${dragging ? "opacity-40" : ""}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-sans-ui text-[13px] font-semibold text-ink truncate">{lead.brand_name}</p>
        <p className="font-sans-ui text-[12px] text-ink/55 truncate">{lead.full_name}</p>
      </div>
      <GripVertical size={14} className="text-ink/25 shrink-0 group-hover:text-ink/50" />
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans-ui text-[11px] text-ink/50">
      <span>{lead.country}</span>
      {lead.monthly_orders && <span>· {lead.monthly_orders} ord/mese</span>}
    </div>
    {lead.notes && <p className="mt-2 font-sans-ui text-[11px] text-ink/60 line-clamp-2 italic">{lead.notes}</p>}
    <div className="mt-3 flex items-center justify-between">
      <FollowUpBadge lead={lead} />
      <span className="font-sans-ui text-[10px] text-ink/35">{(lead.created_at || "").slice(5, 10).split("-").reverse().join("/")}</span>
    </div>
  </motion.div>
);

export const LeadKanban = ({ leads, token, onOpen, onMoved }) => {
  const [over, setOver] = useState(null);
  const [dragging, setDragging] = useState(null);

  const drop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    setOver(null);
    setDragging(null);
    const lead = leads.find((l) => l.id === id);
    if (!lead || (lead.status || "nuovo") === status) return;
    onMoved({ ...lead, status }, false);
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      onMoved(data, true);
    } catch {
      onMoved(lead, true);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="lead-kanban">
      {STATUSES.map((s) => {
        const items = leads.filter((l) => (l.status || "nuovo") === s.key);
        return (
          <div
            key={s.key}
            data-testid={`kanban-column-${s.key}`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== s.key) setOver(s.key); }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => drop(e, s.key)}
            className={`flex flex-col bg-linen/70 border transition-colors min-h-[320px] ${over === s.key ? "border-ink bg-goldtan/20" : "hairline"}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b hairline">
              <span className={`px-2 py-1 font-sans-ui text-[9px] tracking-mega ${s.cls}`}>{s.label.toUpperCase()}</span>
              <span data-testid={`kanban-count-${s.key}`} className="font-sans-ui text-[11px] text-ink/45">{items.length}</span>
            </div>
            <div className="flex-1 p-3 space-y-3" onDragStart={(e) => setDragging(e.target?.dataset?.testid)}>
              {items.length === 0 && (
                <p className="font-sans-ui text-[11px] text-ink/35 text-center py-10">Trascina qui un lead</p>
              )}
              {items.map((l) => (
                <Card key={l.id} lead={l} onOpen={onOpen} dragging={dragging === `kanban-card-${l.id}`} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
